import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { getPlural } from 'client/utils/plural';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { getAdImageUrl } from 'site-modules/shared/components/native-ad/utils/utils';
import {
  svgCarTrims,
  svgColorPalette,
  svgPriceTag,
  svgStreeringWheel,
} from 'client/images/inline-svgs/ads/buildprice-ad-icons';
import { CpoFeaturesList } from 'site-modules/shared/components/native-ad/native-ad-components/cpo-features/cpo-features-list';

import { Styles } from './buildprice-site-served-ad-styles';

const renderJellybean = (vehicleImg, photoDisclaimer, classes) => (
  <figure
    style={Styles.imgWrapper}
    className={classnames('w-100 mx-auto d-flex justify-content-center align-items-center pos-r', classes)}
  >
    <img style={Styles.coreImg} className="img-fluid rounded" src={vehicleImg} alt="" />
    {photoDisclaimer && (
      <figcaption className="w-100 text-end size-12 pos-a mb-0_25" style={Styles.figcaption}>
        {photoDisclaimer}
      </figcaption>
    )}
  </figure>
);

const renderAtGlanceItem = (icon, text, children) => (
  <li style={Styles.li}>
    {icon}
    <ContentFragment componentToUse="span" classes="text-gray-darker">
      {text}
    </ContentFragment>
    {children}
  </li>
);

const renderAtAGlance = creativeConfigData => {
  const { msrp: msrpConfig, trimNames, vehicleInfo } = creativeConfigData;
  const { extColorCnt, pkgCnt } = get(vehicleInfo, 'submodels[0]', {});
  const trimCnt = trimNames?.length;
  const makeName = vehicleInfo?.makeName;
  const msrp = msrpConfig?.trim();

  if (!msrp && !extColorCnt && !trimCnt && !pkgCnt) {
    return (
      <section className="at-a-glance-container pos-r">
        <span className="d-block mb-1 pe-md-1 text-gray-darker">
          Choose the trim, color, options, packages and more {!!makeName && `for your ${makeName}`}
        </span>
      </section>
    );
  }

  return (
    <section className="at-a-glance-container pos-r">
      <header className="size-14 mb-1_5">At a Glance:</header>
      <ul style={Styles.ul}>
        {!!extColorCnt &&
          renderAtGlanceItem(svgColorPalette, `${extColorCnt} ${getPlural('Color', extColorCnt)}`, null)}
        {!!trimCnt && renderAtGlanceItem(svgCarTrims, `${trimCnt} ${getPlural('Trim', trimCnt)}`, null)}
        {!!pkgCnt && renderAtGlanceItem(svgStreeringWheel, `${pkgCnt} ${getPlural('Package', pkgCnt)}`, null)}
        {!!msrp && renderAtGlanceItem(svgPriceTag, msrp, <span className="size-12 ms-0_25">starting MSRP</span>)}
      </ul>
    </section>
  );
};

export function BuildPriceAtAGlance({
  creativeConfigData,
  ctaURL,
  ctaTarget,
  adClickTrackingListener,
  isConquest,
  mobile,
  showOnlyNarrowView = false,
  isCpoFeaturesEnabled = false,
  hideJelly = false,
}) {
  const { headline, cta, linkDisplayUrl, photoDisclaimer, photo, logo, vehicleInfo } = creativeConfigData;

  const vehicleImg = getAdImageUrl(photo);

  const suppressVehicleImage = hideJelly && !isConquest;

  const header = <ContentFragment>{headline}</ContentFragment>;

  const isNarrowClass = 'hidden-md-up';
  const isWideClass = 'hidden-md-down';

  return (
    <Row>
      <Col xs={12} md={showOnlyNarrowView ? null : 6} tag="section">
        {!isConquest && <img className="pos-a" alt="" src={logo} height={'24'} style={{ top: '20px' }} />}
        <header
          className={classnames('headline fw-bold text-gray-darkest mb-md-0_75', {
            'size-20': !mobile,
            'mt-1_5': !isConquest,
            'mt-1 mt-md-1_5': isConquest,
          })}
        >
          {header}
        </header>
        {!suppressVehicleImage && renderJellybean(vehicleImg, photoDisclaimer, classnames(isNarrowClass))}
        {isCpoFeaturesEnabled ? <CpoFeaturesList make={vehicleInfo?.makeName} /> : renderAtAGlance(creativeConfigData)}
      </Col>
      <Col
        xs={12}
        md={showOnlyNarrowView ? null : 6}
        tag="section"
        className="d-flex flex-column justify-content-center"
      >
        {!suppressVehicleImage && renderJellybean(vehicleImg, photoDisclaimer, isWideClass)}
        <Row className="w-100 text-center cta-wrapper mx-auto mt-1_5 mt-md-0">
          <a
            className="site-served-ad-link no-focus text-decoration-none"
            href={ctaURL}
            rel="noopener noreferrer"
            target={ctaTarget}
            onClick={adClickTrackingListener}
          >
            <span className="btn btn-sm btn-outline-blue-20 py-0_5 px-0_75 px-md-0_5 size-16 fw-medium pos-r mt-0 d-block">
              <CtaButtonText text={cta} />
            </span>
          </a>
          <span className="medium text-blue-20 pos-r d-block oem-url-wrapper">{linkDisplayUrl}</span>
        </Row>
      </Col>
    </Row>
  );
}

BuildPriceAtAGlance.propTypes = {
  creativeConfigData: PropTypes.shape({}).isRequired,
  ctaURL: PropTypes.string.isRequired,
  ctaTarget: PropTypes.string.isRequired,
  adClickTrackingListener: PropTypes.func.isRequired,
  mobile: PropTypes.bool.isRequired,
  isConquest: PropTypes.bool.isRequired,
  showOnlyNarrowView: PropTypes.bool,
  isCpoFeaturesEnabled: PropTypes.bool,
  hideJelly: PropTypes.bool,
};
